import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import ErrorMessage from "../../components/ErrorMessages/ErrorMessage";
import { setOtp, setErrorMessage, resetOtp } from "./otpSlice";

const Otp: React.FC = () => {
  const dispatch = useDispatch();
  const otp = useSelector((state: any) => state.otp.otp);
  const errorMessage = useSelector((state: any) => state.otp.errorMessage);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const firstRender = useRef(true);

  useEffect(() => {
    // Reset OTP on component mount
    dispatch(resetOtp());
    
    // Set a small delay to ensure DOM is ready before focusing
    const timer = setTimeout(() => {
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }, 0);
    
    return () => clearTimeout(timer);
  }, [dispatch]);

  // Additional useEffect for focus to handle possible race conditions
  useEffect(() => {
    if (firstRender.current && inputRefs.current[0]) {
      inputRefs.current[0].focus();
      firstRender.current = false;
    }
  }, [otp]);

  const handleChange = (value: string, index: number) => {
    const numericValue = value.replace(/[^0-9]/g, "");
    if (numericValue.length > 1) return; // Prevent multiple digits in one box
    dispatch(setErrorMessage(""));

    const updatedOtp = [...otp];
    updatedOtp[index] = numericValue;
    dispatch(setOtp({ otp: updatedOtp }));

    // Move to the next box automatically
    if (numericValue && index < otp.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setTimeout(() => setCaretToEnd(index - 1), 0);
    }

    if (e.key === "ArrowRight" && index < otp.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
        setTimeout(() => setCaretToEnd(index - 1), 0);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, otp.length);
    const updatedOtp = [...otp];

    pasteData.split("").forEach((digit, i) => {
      if (i < otp.length) {
        updatedOtp[i] = digit;
      }
    });

    dispatch(setOtp({ otp: updatedOtp }));

    // Focus the last filled input box
    const nextIndex = pasteData.length >= otp.length ? otp.length - 1 : pasteData.length;
    inputRefs.current[nextIndex]?.focus();
  };

  const setCaretToEnd = (index: number) => {
    const input = inputRefs.current[index];
    if (input) {
      const length = input.value.length;
      input.setSelectionRange(length, length);
    }
  };

  // Click handler for focusing the first empty input
  const handleClickContainer = () => {
    // Find the first empty input or default to the first one
    const firstEmptyIndex = otp.findIndex(digit => !digit);
    const indexToFocus = firstEmptyIndex === -1 ? 0 : firstEmptyIndex;
    
    inputRefs.current[indexToFocus]?.focus();
  };

  return (
    <>
      <div 
        className={`flex flex-col items-center mt-5 ${errorMessage ? '' : 'mb-[26px]'}`}
        onClick={handleClickContainer}
      >
        <div className="flex gap-4">
          {otp.map((digit: string, index: number) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              id={`digit-${index}`}
              maxLength={1}
              autoComplete="off"
              value={digit}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              placeholder="*"
              className={`w-[54px] h-14 text-center text-lg border rounded-md ${
                errorMessage
                  ? "border-negative"
                  : "border-gray-300 focus:border-blue-500"
              } focus:outline-none placeholder-gray-400 transition-colors`}
              autoFocus={index === 0} // Add autoFocus to the first input
            />
          ))}
        </div>
      </div>
      {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
    </>
  );
};

export default Otp;